import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../../theme';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export default function LoungePolicyBanner() {
    const { t } = useTranslation();
    const [isAfter6PM, setIsAfter6PM] = useState(false);

    useEffect(() => {
        const checkTime = () => {
            const now = new Date();
            const currentHour = now.getHours();
            // Active between 18:00 (6 PM) and 23:59 (midnight)
            setIsAfter6PM(currentHour >= 18);
        };

        checkTime();
        // Check every minute
        const interval = setInterval(checkTime, 60000);

        return () => clearInterval(interval);
    }, []);

    const rules = t('menu.loungePolicy.rules', { returnObjects: true });

    return (
        <Paper
            elevation={0}
            sx={{
                background: isAfter6PM
                    ? 'linear-gradient(135deg, rgba(255,107,0,0.15) 0%, rgba(255,159,74,0.1) 100%)'
                    : 'rgba(255,255,255,0.03)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 2, md: 2.5 },
                p: { xs: 1.5, sm: 2, md: 2.5 },
                mb: { xs: 2.5, md: 3 },
                border: isAfter6PM
                    ? { xs: '1.5px solid rgba(255,107,0,0.5)', md: '2px solid rgba(255,107,0,0.5)' }
                    : '1px solid rgba(255,255,255,0.1)',
                boxShadow: isAfter6PM
                    ? { xs: '0 4px 16px rgba(255,107,0,0.25)', md: '0 6px 24px rgba(255,107,0,0.3)' }
                    : '0 2px 12px rgba(0,0,0,0.2)',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': isAfter6PM ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: { xs: '2px', md: '3px' },
                    background: 'linear-gradient(90deg, #ff6b00, #ff9f4a, #ffd700, #ff9f4a, #ff6b00)',
                    backgroundSize: '200% 100%',
                    animation: 'gradient-shift 3s ease infinite',
                    '@keyframes gradient-shift': {
                        '0%, 100%': { backgroundPosition: '0% 50%' },
                        '50%': { backgroundPosition: '100% 50%' }
                    }
                } : {}
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                mb: { xs: 1.5, md: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.25, md: 1.5 },
                    flex: 1,
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <Box
                        sx={{
                            background: isAfter6PM
                                ? 'linear-gradient(135deg, #ff6b00, #ff9f4a)'
                                : 'rgba(255,107,0,0.3)',
                            borderRadius: { xs: '8px', md: '10px' },
                            p: { xs: 1, md: 1.25 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isAfter6PM ? '0 3px 12px rgba(255,107,0,0.4)' : 'none',
                            animation: isAfter6PM ? 'pulse 2s ease-in-out infinite' : 'none',
                            flexShrink: 0,
                            '@keyframes pulse': {
                                '0%, 100%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.05)' }
                            }
                        }}
                    >
                        {isAfter6PM ? (
                            <EventSeatIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 }, color: '#fff' }} />
                        ) : (
                            <AccessTimeIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 }, color: '#ff9f4a' }} />
                        )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.6, md: 0.75 },
                                flexWrap: 'wrap',
                                mb: 0.25
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    color: isAfter6PM ? '#ffd700' : '#ff9f4a',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {t('menu.loungePolicy.title')}
                            </Typography>
                            {isAfter6PM && (
                                <Box
                                    component="span"
                                    sx={{
                                        background: 'linear-gradient(135deg, #ff6b00, #ffd700)',
                                        color: '#000',
                                        px: { xs: 0.9, md: 1.2 },
                                        py: { xs: 0.25, md: 0.4 },
                                        borderRadius: { xs: 1.25, md: 1.5 },
                                        fontSize: { xs: '0.6rem', md: '0.7rem' },
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        animation: 'blink 2s ease-in-out infinite',
                                        whiteSpace: 'nowrap',
                                        '@keyframes blink': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.7 }
                                        }
                                    }}
                                >
                                    {t('menu.loungePolicy.activeNow')}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ pt: { xs: 1.5, md: 2 }, borderTop: '1px solid rgba(255,107,0,0.2)' }}>
                <Typography
                    sx={{
                        fontFamily: FONT_FAMILY,
                        color: '#d0d0d0',
                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                        mb: { xs: 1.5, md: 2 },
                        lineHeight: 1.6
                    }}
                >
                    {t('menu.loungePolicy.description')}
                </Typography>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: { xs: 1, sm: 1.25, md: 1.5 }
                }}>
                    {rules.map((rule, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.9, md: 1.2 },
                                p: { xs: 1, md: 1.25 },
                                background: 'rgba(255,107,0,0.1)',
                                borderRadius: { xs: 1.25, md: 1.5 },
                                border: '1px solid rgba(255,107,0,0.2)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(255,107,0,0.15)',
                                    transform: { xs: 'none', md: 'translateX(4px)' }
                                }
                            }}
                        >
                            <AutoAwesomeIcon
                                sx={{
                                    color: '#ff9f4a',
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                    flexShrink: 0
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    color: '#fff',
                                    fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                                    fontWeight: 500,
                                    lineHeight: 1.3
                                }}
                            >
                                {rule}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
}
